import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Star, MapPin, Navigation, Loader2, SlidersHorizontal, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CuisineComboboxMulti } from "@/components/cuisine-combobox";
import { REGIONS } from "@/lib/chef-taxonomy";
import { TierBadge } from "@/components/chef-badges";
import { ChefMap, sortByDistance, withCoords } from "@/components/chef-map";
import { chefQueries } from "@/lib/chefs.queries";
import { toast } from "sonner";

export const Route = createFileRoute("/mappa")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Mappa Chef in Italia — ChefConnect" },
      { name: "description", content: "Trova lo chef privato più vicino a te sulla mappa d'Italia. Chef Premium evidenziati in oro." },
      { property: "og:title", content: "Mappa Chef in Italia — ChefConnect" },
      { property: "og:description", content: "Tutti gli chef privati su mappa interattiva. Premium evidenziati in oro." },
      { property: "og:url", content: "https://chefconnectitalia.com/mappa" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://chefconnectitalia.com/__l5e/assets-v1/a2ae4b98-4144-41f7-845e-72f224ca696b/og-home.jpg" },
      { name: "twitter:image", content: "https://chefconnectitalia.com/__l5e/assets-v1/a2ae4b98-4144-41f7-845e-72f224ca696b/og-home.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://chefconnectitalia.com/mappa" }],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(chefQueries.list(200)),
  errorComponent: ({ error }) => (
    <div className="container mx-auto max-w-2xl px-4 py-24 text-center">
      <h1 className="font-display text-3xl">Impossibile caricare la mappa</h1>
      <p className="mt-3 text-sm text-muted-foreground">{error.message}</p>
    </div>
  ),
  component: MapPage,
});

function MapPage() {
  const { data: chefs } = useSuspenseQuery(chefQueries.list(200));
  const [query, setQuery] = useState("");
  const [region, setRegion] = useState("");
  const [cuisines, setCuisines] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 300]);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [pos, setPos] = useState<[number, number] | null>(null);
  const [locating, setLocating] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const cuiSet = new Set(cuisines);
    const base = chefs.filter((c) => {
      if (q) {
        const hit =
          (c.city ?? "").toLowerCase().includes(q) ||
          (c.region ?? "").toLowerCase().includes(q) ||
          c.display_name.toLowerCase().includes(q) ||
          c.cuisines.some((x) => x.toLowerCase().includes(q));
        if (!hit) return false;
      }
      if (region && c.region !== region) return false;
      if (cuiSet.size > 0 && !c.cuisines.some((x) => cuiSet.has(x))) return false;
      if (verifiedOnly && !c.verified_badge) return false;
      const priceFrom = c.min_price ?? c.hourly_rate ?? 0;
      if (priceFrom < priceRange[0] || priceFrom > priceRange[1]) return false;
      return true;
    });
    return withCoords(base);
  }, [chefs, query, region, cuisines, verifiedOnly, priceRange]);

  const ranked = useMemo(() => (pos ? sortByDistance(filtered, pos) : null), [filtered, pos]);

  const activeFilters =
    (region ? 1 : 0) + (cuisines.length > 0 ? 1 : 0) + (verifiedOnly ? 1 : 0) +
    (priceRange[0] !== 0 || priceRange[1] !== 300 ? 1 : 0);

  const resetFilters = () => {
    setRegion(""); setCuisines([]); setPriceRange([0, 300]); setVerifiedOnly(false);
  };

  const locate = () => {
    if (!("geolocation" in navigator)) { toast.error("Geolocalizzazione non supportata"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setPos([p.coords.latitude, p.coords.longitude]); setLocating(false); toast.success("Posizione trovata"); },
      (err) => {
        setLocating(false);
        toast.error(err.code === err.PERMISSION_DENIED ? "Permesso negato. Abilita la posizione." : "Impossibile rilevare la posizione");
      },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col">
      <div className="border-b border-border bg-card/95 backdrop-blur">
        <div className="container mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-gold">Mappa</p>
            <h1 className="font-display text-2xl sm:text-3xl">Chef vicino a te</h1>
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Star className="h-4 w-4 fill-[#D4AF37] text-[#7A5C00]" /> Gold
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Star className="h-4 w-4 fill-[#C0C0C0] text-[#6B7280]" /> Silver
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Star className="h-4 w-4 fill-foreground text-foreground" /> Standard
            </span>
          </div>
        </div>
        <div className="container mx-auto max-w-7xl px-4 pb-4 sm:px-6">
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-border bg-background px-3 py-2.5 focus-within:ring-1 focus-within:ring-gold">
              <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
              <Input aria-label="Cerca città, regione, cucina o nome chef" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca città, regione o cucina…" className="min-w-0 flex-1 border-0 bg-transparent p-0 shadow-none focus-visible:ring-0" />
              <span className="hidden shrink-0 whitespace-nowrap text-xs text-muted-foreground sm:inline">{filtered.length} chef</span>
            </label>
            <Button
              type="button"
              variant="outline"
              onClick={() => setFiltersOpen((v) => !v)}
              aria-expanded={filtersOpen}
            >
              <SlidersHorizontal className="mr-2 h-4 w-4" />
              Filtri{activeFilters > 0 ? ` · ${activeFilters}` : ""}
            </Button>
            <Button onClick={locate} disabled={locating} className="bg-gold text-gold-foreground hover:bg-gold/90">
              {locating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Navigation className="mr-2 h-4 w-4" />}
              Vicino a me
            </Button>
          </div>
          {filtersOpen && (
            <div className="mt-3 grid gap-4 rounded-lg border border-border bg-background/60 p-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Regione</Label>
                <Select value={region || "__all"} onValueChange={(v) => setRegion(v === "__all" ? "" : v)}>
                  <SelectTrigger aria-label="Filtra per regione" className="mt-2 w-full">
                    <SelectValue placeholder="Tutte le regioni" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all">Tutte le regioni</SelectItem>
                    {REGIONS.map((r) => (<SelectItem key={r} value={r}>{r}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Prezzo · €{priceRange[0]} – €{priceRange[1]}
                </Label>
                <Slider
                  value={priceRange}
                  onValueChange={(v) => setPriceRange([v[0], v[1]] as [number, number])}
                  min={0} max={300} step={10} className="mt-4"
                />
              </div>
              <div className="lg:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Tipo di cucina</Label>
                <div className="mt-2">
                  <CuisineComboboxMulti
                    value={cuisines}
                    onChange={setCuisines}
                    placeholder="Tutte le cucine"
                    ariaLabel="Filtra per tipo di cucina"
                  />
                </div>
              </div>
              <label className="flex cursor-pointer items-center gap-3 rounded-md border border-border bg-secondary/40 p-3">
                <Checkbox checked={verifiedOnly} onCheckedChange={(v) => setVerifiedOnly(v === true)} />
                <span className="text-sm">Solo chef verificati</span>
              </label>
              {activeFilters > 0 && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1 self-end justify-self-start text-xs text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3 w-3" /> Azzera filtri
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="relative flex flex-1 overflow-hidden">
        <div className="relative flex-1">
          <ChefMap chefs={filtered} userPosition={pos} />
          {(chefs.length === 0 || filtered.length === 0) && (
            <div className="pointer-events-none absolute inset-0 grid place-items-center bg-background/70 backdrop-blur-sm">
              <div className="pointer-events-auto max-w-md rounded-xl border border-border bg-card p-6 text-center shadow-xl">
                <h2 className="font-display text-2xl">Nessuno chef disponibile in questa zona</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Stiamo costruendo la rete di ChefConnect. Torna presto, oppure diventa il primo chef della tua città.
                </p>
                <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                  {chefs.length > 0 && (
                    <Button variant="outline" onClick={() => { setQuery(""); resetFilters(); }}>
                      Cambia città
                    </Button>
                  )}
                  <Button asChild className="bg-gold text-gold-foreground hover:bg-gold/90">
                    <Link to="/diventa-chef">Diventa Chef</Link>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
        {ranked && ranked.length > 0 && (
          <aside className="hidden w-80 shrink-0 overflow-y-auto border-l border-border bg-card md:block">
            <div className="border-b border-border p-4">
              <p className="text-xs font-medium uppercase tracking-wider text-gold">Più vicini a te</p>
              <p className="text-xs text-muted-foreground">{ranked.length} risultati · ordinati per distanza</p>
            </div>
            <ul className="divide-y divide-border">
              {ranked.slice(0, 20).map(({ chef, km }) => (
                <li key={chef.user_id}>
                  <Link to="/chef/$slug" params={{ slug: chef.slug }} className="flex items-center gap-3 p-3 transition hover:bg-secondary/50">
                    {chef.cover_url ? (
                      <img src={chef.cover_url} alt={chef.display_name} className="h-12 w-12 rounded-md object-cover" />
                    ) : (
                      <div className="grid h-12 w-12 place-items-center rounded-md bg-secondary text-xs font-semibold">{chef.display_name.slice(0, 2).toUpperCase()}</div>
                    )}
                    <div className="flex min-w-0 flex-1 flex-col">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate text-sm font-medium">{chef.display_name}</span>
                        <TierBadge tier={chef.premium_tier} className="px-1 py-0 text-[9px]" />
                      </div>
                      <span className="truncate text-xs text-muted-foreground">{chef.city ?? ""}</span>
                    </div>
                    <span className="shrink-0 text-xs font-medium text-gold">{km.toFixed(0)} km</span>
                  </Link>
                </li>
              ))}
            </ul>
          </aside>
        )}
      </div>
    </div>
  );
}